import { useToasts } from "@skailer-com/uikit";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

declare global {
  interface Window {
    __APP_VERSION__: string;
  }
}

interface VersionCheckerProps {
  checkInterval?: number; // ms
  toastInterval?: number; // ms
}

const LAST_TOAST_KEY = "version_updater_last_toast";
const LAST_VERSION_KEY = "version_updater_last_version";

const VersionUpdater: React.FC<VersionCheckerProps> = ({
  checkInterval = 5 * 60 * 1000,
  toastInterval = 2 * 60 * 60 * 1000,
}) => {
  const { showInfo } = useToasts();
  const { t } = useTranslation();

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const canShowToast = () => {
      const lastToastTime = localStorage.getItem(LAST_TOAST_KEY);
      if (!lastToastTime) return true;

      const timeSinceLastToast = Date.now() - parseInt(lastToastTime);
      return timeSinceLastToast >= toastInterval;
    };

    const shouldShowToast = (newVersion: string) => {
      const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
      return !lastVersion || lastVersion !== newVersion;
    };

    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          headers: { "Cache-Control": "no-cache" },
        });

        if (!response.ok) return;

        const { version } = await response.json();

        if (window.__APP_VERSION__ !== version && canShowToast() && shouldShowToast(version)) {
          analyticsService.trackEvent(analyticEvents.app.newVersionAvailable);
          localStorage.setItem(LAST_TOAST_KEY, Date.now().toString());
          localStorage.setItem(LAST_VERSION_KEY, version);
          showInfo({
            title: t("core:app_update_available_title"),
            description: t("core:app_update_available_description"),
            button: t("core:app_update_available_button"),
            onClick: () => {
              analyticsService.trackEvent(analyticEvents.pageActions.newVersionUpdateClicked);
              window.location.reload();
            },
            duration: Infinity,
            showCloseButton: false,
          });
        }
      } catch (error) {
        console.error("Failed to check version:", error);
      }
    };

    const startVersionChecking = () => {
      checkVersion();
      intervalId = setInterval(checkVersion, checkInterval);
    };

    const stopVersionChecking = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startVersionChecking();
      } else {
        stopVersionChecking();
      }
    };

    if (document.visibilityState === "visible") {
      startVersionChecking();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopVersionChecking();
    };
  }, [checkInterval, toastInterval, showInfo, t]);

  return null;
};

export default VersionUpdater;
